import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../hooks/useprofile';
import { profileService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';

const AdminProfile = () => {
  const navigate = useNavigate();
  const { profile, loading, refetch } = useProfile();
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    paragraph1: '',
    paragraph2: '',
    paragraph3: ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        paragraph1: profile.paragraph1 || '',
        paragraph2: profile.paragraph2 || '',
        paragraph3: profile.paragraph3 || ''
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let result;
      if (profile?.id) {
        // Update existing profile
        result = await profileService.update(profile.id, formData);
      } else {
        // Create new profile
        result = await profileService.create(formData);
      }

      if (result.success) {
        toast.success('Profile updated successfully!');
        refetch();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Error updating profile');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Edit Profile</h1>
        </div>

        {loading ? (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading profile...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Paragraph 1 */}
            <div>
              <label className="block text-white/60 text-sm mb-2">First Paragraph</label>
              <textarea
                name="paragraph1"
                value={formData.paragraph1}
                onChange={handleChange}
                rows={4}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Enter the first paragraph of your about section..."
              />
            </div>

            {/* Paragraph 2 */}
            <div>
              <label className="block text-white/60 text-sm mb-2">Second Paragraph</label>
              <textarea
                name="paragraph2"
                value={formData.paragraph2}
                onChange={handleChange}
                rows={4}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Enter the second paragraph of your about section..."
              />
            </div>

            {/* Paragraph 3 */}
            <div>
              <label className="block text-white/60 text-sm mb-2">Third Paragraph</label>
              <textarea
                name="paragraph3"
                value={formData.paragraph3}
                onChange={handleChange}
                rows={4}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Enter the third paragraph of your about section..."
              />
            </div>

            {/* Preview */}
            <div className="p-6 rounded-lg border border-white/10 bg-white/5">
              <h3 className="text-sm text-white/40 uppercase tracking-wider mb-4">Preview</h3>
              <div className="space-y-4 text-white/90 leading-relaxed">
                {formData.paragraph1 && <p>{formData.paragraph1}</p>}
                {formData.paragraph2 && <p>{formData.paragraph2}</p>}
                {formData.paragraph3 && <p>{formData.paragraph3}</p>}
                {!formData.paragraph1 && !formData.paragraph2 && !formData.paragraph3 && (
                  <p className="text-white/30 italic">Your profile content will appear here...</p>
                )}
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};

export default AdminProfile;